import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import { Box, Callout, CopyButton } from '@node-space/ui-components'
import { PageHeader } from 'components/header/PageHeader'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useGetCustomersWalletQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { useGetWalletLegacyId } from 'hooks/queries/Wallets/useGetWalletLegacyId'
import {
  currentCustomerAtom,
  currentCustomerWalletAtom,
} from 'pages/Customers/atoms/customersAtoms'
import { WalletDetails } from 'pages/WalletDetails/WalletDetails'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryKeys, reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import {
  CustomerWalletDetails,
  CustomerWalletStatusType,
  NestedCustomerDetails,
} from 'types/customers'
import { WalletTypes } from 'types/types'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import CustomerWalletPageHeader from './components/CustomerWallet/CustomerWalletPageHeader'
import { WalletActionButtons } from './components/WalletActionButtons'
import { CustomerWalletPageParams } from './types'

export const NestedCustomerWallet = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { customerReference, customerWalletId }: CustomerWalletPageParams = useParams()
  const { enableFiatPayinCapabilityV2, enableFiatPayoutCapabilityV2 } = useFeatureFlags()

  const { profile } = useProfileContext() || {}
  const [, setWalletAction] = useContext(WalletActionContext)

  const setCurrentCustomerAtom = useSetAtom(currentCustomerAtom)
  const setCurrentCustomerWalletAtom = useSetAtom(currentCustomerWalletAtom)
  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)

  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(null)
  const [isPayWalletModalOpen, setIsPayWalletModalOpen] = useState(false)

  const { data: customer, isFetching: isFetchingCustomer } =
    useCustomerQuery<NestedCustomerDetails>(customerReference)

  const { data: customerWallet, isFetching: isFetchingCustomerWallet } = useGetCustomersWalletQuery(
    customerWalletId,
    !!customerWalletId
  )

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (customerWallet?.id) {
      return getWalletActionCapabilityRequestFilters(
        [customerWallet?.id],
        enableFiatPayinCapabilityV2,
        enableFiatPayoutCapabilityV2
      )
    }
  }, [customerWallet?.id])

  const { data: bulkCapabilities, isFetching: isFetchingCapabilities } = useBulkCapabiltiesQuery(
    fiatWalletCapabilitiesRequest,
    !!fiatWalletCapabilitiesRequest
  )

  useEffect(() => {
    setCurrentCustomerAtom(customer)
    return () => {
      setCurrentCustomerAtom(null)
    }
  }, [customer])

  useEffect(() => {
    setCurrentCustomerWalletAtom(customerWallet)
    return () => {
      setCurrentCustomerWalletAtom(null)
    }
  }, [customerWallet])

  const { data: walletId, isFetching: isFetchingWalletLegacyId } = useGetWalletLegacyId(
    customerWallet?.id
  )

  const isLoading = useMemo(() => {
    return isFetchingCustomerWallet ?? isFetchingCustomer ?? isFetchingWalletLegacyId
  }, [isFetchingCustomerWallet, isFetchingCustomer, isFetchingWalletLegacyId])

  const customerWalletDetails: CustomerWalletDetails = useMemo(() => {
    return {
      ...customerWallet,
      legacyWalletId: walletId,
    }
  }, [walletId, customerWallet])

  const isReadOnlyWallet =
    customerWallet?.status === CustomerWalletStatusType.INACTIVE ||
    customerWallet?.status === CustomerWalletStatusType.TERMINATED

  const refreshWallets = async () => {
    await queryClient.invalidateQueries({
      queryKey: reactQueryKeys.walletsV2({ walletType: WalletTypes.FIAT }),
    })
    await queryClient.invalidateQueries({
      queryKey: [reactQueryName.CUSTOMER_WALLETS],
    })
    await queryClient.invalidateQueries({
      queryKey: reactQueryKeys.totalBalance(profile?.currencyCode),
    })
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  return (
    <>
      <PageHeader
        description={
          <CustomerWalletPageHeader customerWallet={customerWallet} isLoading={isLoading} />
        }
        renderRightSection={
          <Box flex gapX={12}>
            {customerWallet && customer && (
              <Box flex flexCol gapY={8} className="sm:items-end">
                <Box flex align="center" gap={4} paddingX={4}>
                  <p className="text-body-regular-small text-foreground-subdued">
                    {t('wallets.walletId')}
                    {customerWallet?.id}
                  </p>
                  <CopyButton copyText={customerWallet?.id} />
                </Box>
                {!isReadOnlyWallet && (
                  <Box flex gapX={8}>
                    <WalletActionButtons
                      customer={customer}
                      customerWallet={customerWallet}
                      bulkCapabilities={bulkCapabilities}
                      isFetchingCapabilities={isFetchingCapabilities}
                      isFetchingWalletLegacyId={isFetchingWalletLegacyId}
                      setIsSheetOpen={setIsSheetOpen}
                      cleanupUnifiedPayoutsFlow={cleanupUnifiedPayoutsFlow}
                    />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        }
      />
      <Box flexCol gapX={24} gapY={24} width="full">
        {isReadOnlyWallet && (
          <Callout variant="warning">{t('customers.walletReadOnlyCallout')}</Callout>
        )}
        <WalletDetails customerWalletDetails={customerWalletDetails} />
      </Box>

      <WalletSheet isSheetOpen={isSheetOpen} />

      {isPayWalletModalOpen && (
        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={refreshWallets}
          setShowWalletModal={setIsPayWalletModalOpen}
        />
      )}
    </>
  )
}
